import React from 'react';
import dayjs from 'dayjs';
import { Tag, Button, Divider, Typography } from 'design-system';

import type { ActionPlan } from 'src/services/actionPlan';

// 获取进度状态信息
const getProgressStatus = (record: ActionPlan) => {
  const { status, assessmentDimension, actualEndDate, planEndDate } = record;
  
  // 如果 status 为 null，不管 assessmentDimension 是什么，都显示 "--"
  if (status === null) {
    return { text: '--', color: 'default' };
  }
  
  // 当 assessmentDimension != 'Quantity' 且 status == 'NotStarted' 时，显示"未完成"
  if (assessmentDimension !== 'Quantity' && status === 'NotStarted') {
    return { text: '未完成', color: 'error' };
  }
  
  // 如果状态是未开始（但assessmentDimension为Quantity）
  if (status === 'NotStarted') {
    return { text: '--', color: 'default' };
  }
  
  // 如果状态是已完成
  if (status === 'Completed') {
    // 如果考核维度是Duration，需要比较时间
    if (assessmentDimension === 'Duration' && actualEndDate && planEndDate) {
      const actualEnd = dayjs(actualEndDate);
      const planEnd = dayjs(planEndDate);
      
      if (actualEnd.isBefore(planEnd)) {
        return { text: '提前完成', color: 'success' };
      } if (actualEnd.isSame(planEnd, 'day')) {
        return { text: '按时完成', color: 'processing' };
      } 
        return { text: '延后完成', color: 'warning' };
      
    }
    // 其他维度或没有时间信息时，显示已完成
    return { text: '已完成', color: 'success' };
  }
  
  // 如果状态不是已完成
  if (status === 'Canceled') {
    return { text: '已取消', color: 'default' };
  }
  
  // 其他状态（如InProgress等）
  if (status === 'InProgress') {
    return { text: '进行中', color: 'processing' };
  }
  
  // 默认状态（NotStarted但assessmentDimension为Quantity的情况）
  return { text: '--', color: 'default' };
};

const sourceMap: Record<string, string> = {
  'BusinessPlan': '目标',
};

interface ListPlanPreviewProps {
  plan: ActionPlan;
  x: number;
  y: number;
  onEdit?: (plan: ActionPlan) => void;
  onDetail?: (plan: ActionPlan) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  id?: string;
  onCloseList?: () => void;
}

const ListPlanPreview: React.FC<ListPlanPreviewProps> = ({ plan, x, y, onEdit, onDetail, onMouseEnter, onMouseLeave, id, onCloseList }) => {
  const status = getProgressStatus(plan);
  return (
    <div
      id={id}
      style={{
        position: 'fixed',
        top: y,
        left: x,
        zIndex: 9999,
        width: 360,
        boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
        borderRadius: 8,
        background: '#fff'
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div style={{ padding: '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {plan.code || '行动计划'}
          </Typography.Title>
          {status.text !== '--' && <Tag color={status.color}>{status.text}</Tag>}
        </div>
        <div>
          <div style={{ display: 'flex', marginBottom: '12px' }}>
            <div style={{ width: 90, color: '#666', flexShrink: 0, textAlign: 'right', paddingRight: 8 }}>考核部门：</div>
            <div style={{ flex: 1 }}>{plan.department?.name || '-'}</div>
          </div>
          <div style={{ display: 'flex', marginBottom: '12px' }}>
            <div style={{ width: 90, color: '#666', flexShrink: 0, textAlign: 'right', paddingRight: 8 }}>责任人：</div>
            <div style={{ flex: 1 }}>{plan.coordinators?.map(c => `${c.lastName}${c.firstName}`).join('、') || '-'}</div>
          </div>
          <div style={{ display: 'flex', marginBottom: '12px' }}>
            <div style={{ width: 90, color: '#666', flexShrink: 0, textAlign: 'right', paddingRight: 8 }}>行动计划描述：</div>
            <div style={{ flex: 1, wordBreak: 'break-all', whiteSpace: 'pre-line' }}>{plan.description || '-'}</div>
          </div>
          <div style={{ display: 'flex', marginBottom: '12px' }}>
            <div style={{ width: 90, color: '#666', flexShrink: 0, textAlign: 'right', paddingRight: 8 }}>来源：</div>
            <div style={{ flex: 1 }}>{sourceMap[plan.source as string] || plan.source || '-'}</div>
          </div>
          <div style={{ display: 'flex', marginBottom: '4px' }}>
            <div style={{ width: 90, color: '#666', flexShrink: 0, textAlign: 'right', paddingRight: 8 }}>计划任务周期：</div>
            <div style={{ flex: 1 }}>
              {plan.planStartDate && plan.planEndDate
                ? `${dayjs(plan.planStartDate).format('YYYY-MM-DD')} 至 ${dayjs(plan.planEndDate).format('YYYY-MM-DD')}`
                : '-'}
            </div>
          </div>
        </div>
      </div>
      <Divider style={{ margin: '0' }} />
      {/* 按钮操作区 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px' }}>
        <Button
          type="default"
          style={{
            border: '1px solid #105FC8',
            color: '#105FC8',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            fontWeight: 500,
            borderRadius: 8,
            height: 36,
            padding: '0 20px',
          }}
          onClick={() => { onCloseList && onCloseList(); onEdit && onEdit(plan); }}
        >
          <img src="/assets/icons/common/c-edit.svg" alt="编辑" style={{ width: 16, height: 16, marginRight: 8 }} />
          编辑
        </Button>
        <Button
          type="default"
          style={{
            border: '1px solid #105FC8',
            color: '#105FC8',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            fontWeight: 500,
            borderRadius: 8,
            height: 36,
            padding: '0 20px',
          }}
          onClick={() => { onCloseList && onCloseList(); onDetail && onDetail(plan); }}
        >
          <img src="/assets/icons/common/c-detail.svg" alt="详情" style={{ width: 16, height: 16, marginRight: 8 }} />
          详情
        </Button>
      </div>
    </div>
  );
};

export default ListPlanPreview;